import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { apiErrorMessage } from '@/shared/api-client';
import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import {
  useCreateEmployee,
  useEmployees,
  useUpdateEmployee,
  useUsersForEmployeeLinks,
  type Employee,
} from './employees';

export function EmployeesPage() {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const canManage = auth.can('workforce.employee.manage');
  const canView = auth.can('workforce.employee.view') || canManage;
  const { data, isLoading, isError } = useEmployees();
  const updateEmployee = useUpdateEmployee();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);

  if (!canView) return <Forbidden />;

  async function setActive(employee: Employee, active: boolean) {
    try {
      await updateEmployee.mutateAsync({ id: employee.id, input: { active } });
      toast.success(t(active ? 'employees.toast.activated' : 'employees.toast.deactivated'));
    } catch (caught) {
      toast.error(apiErrorMessage(t, 'employees', caught));
    }
  }

  return (
    <section className="grid gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('employees.title')}</h1>
        {canManage && (
          <Button onClick={() => setCreating(true)}>{t('employees.actions.create')}</Button>
        )}
      </div>

      {isLoading && <p className="text-muted-foreground">{t('employees.loading')}</p>}
      {isError && <p className="text-destructive">{t('employees.errors.loadFailed')}</p>}

      {data && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('employees.columns.name')}</th>
                <th className="px-4 py-2 font-medium">{t('employees.columns.status')}</th>
                <th className="px-4 py-2 font-medium">{t('employees.columns.user')}</th>
                <th className="px-4 py-2 font-medium">{t('employees.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                    {t('employees.empty')}
                  </td>
                </tr>
              )}
              {data.items.map((employee) => (
                <tr key={employee.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{employee.name}</td>
                  <td className="px-4 py-2">
                    {employee.active
                      ? t('employees.status.active')
                      : t('employees.status.inactive')}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {employee.userLabel ?? t('employees.user.unlinked')}
                  </td>
                  <td className="px-4 py-2">
                    {canManage ? (
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(employee)}>
                          {t('employees.actions.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setActive(employee, !employee.active)}
                          disabled={updateEmployee.isPending}
                        >
                          {employee.active
                            ? t('employees.actions.deactivate')
                            : t('employees.actions.activate')}
                        </Button>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{t('employees.readOnly')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateEmployeeDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => toast.success(t('employees.toast.created'))}
      />
      <EditEmployeeDialog
        employee={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => toast.success(t('employees.toast.updated'))}
      />
    </section>
  );
}

function CreateEmployeeDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation('common');
  const createEmployee = useCreateEmployee();
  const users = useUsersForEmployeeLinks(open);
  const [name, setName] = useState('');
  const [userId, setUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setUserId('');
      setError(null);
    }
  }, [open]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await createEmployee.mutateAsync({ name, userId: userId || null });
      onOpenChange(false);
      onCreated();
    } catch (caught) {
      setError(apiErrorMessage(t, 'employees', caught));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('employees.create.title')}</DialogTitle>
            <DialogDescription>{t('employees.create.description')}</DialogDescription>
          </DialogHeader>
          <EmployeeFields
            name={name}
            userId={userId}
            onNameChange={setName}
            onUserIdChange={setUserId}
            users={users.data?.items ?? []}
            usersLoading={users.isLoading}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={createEmployee.isPending}>
              {createEmployee.isPending
                ? t('employees.create.submitting')
                : t('employees.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditEmployeeDialog({
  employee,
  onOpenChange,
  onSaved,
}: {
  employee: Employee | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('common');
  const updateEmployee = useUpdateEmployee();
  const users = useUsersForEmployeeLinks(employee !== null);
  const [name, setName] = useState('');
  const [userId, setUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (employee) {
      setName(employee.name);
      setUserId(employee.userId ?? '');
      setError(null);
    }
  }, [employee]);

  if (!employee) return null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await updateEmployee.mutateAsync({
        id: employee!.id,
        input: { name, userId: userId || null },
      });
      onOpenChange(false);
      onSaved();
    } catch (caught) {
      setError(apiErrorMessage(t, 'employees', caught));
    }
  }

  return (
    <Dialog open={employee !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('employees.edit.title')}</DialogTitle>
            <DialogDescription>{t('employees.edit.description')}</DialogDescription>
          </DialogHeader>
          <EmployeeFields
            name={name}
            userId={userId}
            onNameChange={setName}
            onUserIdChange={setUserId}
            users={users.data?.items ?? []}
            usersLoading={users.isLoading}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={updateEmployee.isPending}>
              {updateEmployee.isPending
                ? t('employees.edit.submitting')
                : t('employees.edit.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type UserOption = { id: string; email: string; displayName: string | null };

function EmployeeFields({
  name,
  userId,
  onNameChange,
  onUserIdChange,
  users,
  usersLoading,
}: {
  name: string;
  userId: string;
  onNameChange: (name: string) => void;
  onUserIdChange: (userId: string) => void;
  users: UserOption[];
  usersLoading: boolean;
}) {
  const { t } = useTranslation('common');

  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor="employee-name">{t('employees.fields.name')}</Label>
        <Input
          id="employee-name"
          required
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="employee-user">{t('employees.fields.user')}</Label>
        <select
          id="employee-user"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          value={userId}
          onChange={(event) => onUserIdChange(event.target.value)}
        >
          <option value="">{t('employees.user.unlinked')}</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.displayName || user.email}
            </option>
          ))}
        </select>
        {usersLoading && (
          <p className="text-sm text-muted-foreground">{t('employees.user.loading')}</p>
        )}
      </div>
    </>
  );
}
